import { Alert } from "@msuite/ui/alert";
import { TiptapDisplay } from "@msuite/ui/libs/tiptap";
import { Link } from "@msuite/ui/link";
import { localized } from "@msuite/ui/localization";
import { createColumnHelper, createRowActions } from "@msuite/ui/table";
import { createdAtFormatter, moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import type { OrganizationAlertListItem, TeamList } from "@msuite/utils/types";
import RenderHooks from "render-hooks";
import { UserCard } from "@/components/cards";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { RowActions } from "./row-actions";

const hooks = { useMentionOptions };

const columnHelper = createColumnHelper<OrganizationAlertListItem>();

/**
 * Resolves an alert's target team IDs to their display names. Returned as a
 * comma-separated string so the column accessor value stays searchable by name
 * rather than by the underlying team IDs.
 */
function resolveTeamNames(teamIds: string[], teams: TeamList) {
	const names: string[] = [];
	for (const teamId of teamIds) {
		for (const team of teams) {
			if (team.teamId === teamId) {
				names.push(team.name);
				break;
			}
		}
	}
	return names.join(", ");
}

export function createColumns(teams: TeamList) {
	return [
		columnHelper.accessor(createdAtFormatter, {
			id: "createdAt",
			header: localized("general_fields.created_at"),
			cell: (cell) => cell.getValue(),
		}),
		columnHelper.accessor("createdBy", {
			id: "createdBy",
			header: localized("general_fields.created_by"),
			cell: (cell) => (
				<UserCard
					userId={cell.getValue()}
					variant="plain"
					withAvatar={false}
				/>
			),
		}),
		columnHelper.accessor((row) => localized(`dashboard.alerts.level_types.${row.levelType}`), {
			id: "level",
			header: localized("dashboard.alerts.alert_level"),
			cell: (cell) => <Alert.Label level={cell.row.original.levelType} />,
		}),
		columnHelper.accessor("title", {
			id: "title",
			header: localized("protocols.title"),
			cell: (cell) => cell.getValue(),
		}),
		columnHelper.accessor("description", {
			id: "description",
			header: localized("general.description"),
			cell: (cell) => (
				<RenderHooks hooks={hooks}>
					{({ useMentionOptions }) => {
						const mentionOptions = useMentionOptions();
						return (
							<TiptapDisplay
								json={cell.getValue() || ""}
								{...mentionOptions}
							/>
						);
					}}
				</RenderHooks>
			),
		}),
		columnHelper.accessor((row) => resolveTeamNames(row.teamIds, teams), {
			id: "teams",
			header: localized("dashboard.alerts.teams"),
			cell: (cell) => guardString(cell.getValue() || null),
		}),
		columnHelper.accessor("startDate", {
			id: "startDate",
			header: localized("dates.start_date"),
			cell: (cell) => (cell.getValue() ? moment(cell.getValue()).dateFormat() : guardString(null)),
		}),
		columnHelper.accessor("endDate", {
			id: "endDate",
			header: localized("dates.end_date"),
			cell: (cell) => (cell.getValue() ? moment(cell.getValue()).dateFormat() : guardString(null)),
		}),
		columnHelper.accessor("href", {
			id: "href",
			header: localized("general.link"),
			cell: (cell) => (
				<Link
					href={cell.getValue()}
					isExternal
				>
					{cell.getValue()}
				</Link>
			),
		}),
		createRowActions(RowActions),
	];
}
